import React, { useContext } from 'react'
import moment from 'moment'
import OfferUrgencyLabels from 'components/Common/Labels/OfferUrgencyLabels'
import NumberRating from 'components/Common/NumberRating'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import SearchTileCancellationPolicy from 'components/SearchV2/SearchTileCancellationPolicy/SearchTileCancellationPolicy'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import Group from 'components/utils/Group'
import { showUserReviewsRating } from 'lib/order/reviewUtils'
import OfferTileLocationSection from '../OfferTileLocationSection'
import OfferTilePropertyHeading from '../OfferTilePropertyHeading'
import TileInclusions from '../../Support/OfferTileInclusions'
import styled from 'styled-components'
import shouldHideAoLabel from 'lib/offer/hideAoLabel'
import { rem } from 'polished'
import { connect } from 'react-redux'
import { getShowOnMapOfferExperimentVariant } from 'selectors/searchSelectors'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { encodeOfferIds } from 'components/Pages/HotelSearchPage/HotelSearchMapPage'
import { queryKeySelectedOfferIds } from 'constants/url'
import { useOfferInclusions } from 'hooks/useOfferInclusions'
import { useAllInclusions } from 'hooks/useAllInclusions'
import { setSearchParamValue } from 'lib/url/searchUrlUtils'
import useSearchTileInclusionUpsellBestPrice from 'hooks/useSearchTileInclusionUpsellBestPrice'
import { useTacticalUpsellBanner } from 'hooks/useTacticalUpsellBanner'
import { OFFER_TYPE_HOTEL, OFFER_TYPE_LAST_MINUTE } from 'constants/offer'

const PropertyDetails = styled.div`
  > * + * {
    margin-top: ${rem(4)};
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

interface MappedStateProps {
  shouldShowMapButton: boolean;
  windowSearch: string;
}

interface Props {
  location: string;
  cancellationPolicyType: string;
  offer: App.OfferSummary;
  checkInDate: moment.Moment | undefined;
  filters: App.OfferListFilters | undefined;
  offerUrl: string;
  bestPricePackage?: App.Package;
  showCompact?: boolean;
  maxInclusions?: number;
  maxInclusionLines?: number;
}

function SearchTilePropertyDetails(props: Props & MappedStateProps) {
  const {
    shouldShowMapButton,
    location,
    cancellationPolicyType,
    offer,
    checkInDate,
    windowSearch,
    offerUrl,
    filters,
    bestPricePackage,
    showCompact = false,
    maxInclusions,
    maxInclusionLines,
  } = props

  const {
    property,
    name,
    tileInclusions,
    tileInclusionsHeading,
    tileDescription,
    type,
    productType,
    id,
  } = offer

  const { searchTargetLabel, offerDistanceFromSearchTarget, searchType } =
    useContext(GlobalSearchStateContext)
  const searchTargetDistance = offerDistanceFromSearchTarget?.[id]
  const showSearchDistance =
    searchType === 'landmark' &&
    !!searchTargetLabel &&
    typeof searchTargetDistance === 'number'

  const hideAoLabel = shouldHideAoLabel(offer)

  let inclusionsHeading: string

  if (type === OFFER_TYPE_LAST_MINUTE) {
    inclusionsHeading = tileInclusionsHeading ?? 'Exclusive extras:'
  } else {
    inclusionsHeading = tileInclusionsHeading ?? 'Handpicked inclusions:'
  }

  const { shouldShowUpsell: showInclusionUpsell } =
    useSearchTileInclusionUpsellBestPrice(offer, filters)

  const shouldShowLPCTacticalUpsell = useTacticalUpsellBanner(offer, bestPricePackage, filters)
  const showLPCTacticalLabel = offer?.hasTactical && bestPricePackage?.hasTactical && !shouldShowLPCTacticalUpsell
  if (showLPCTacticalLabel) {
    inclusionsHeading = 'Your handpicked inclusions:'
  }

  const { inclusions: commonInclusions, bonusInclusions, tacticalInclusions } = useOfferInclusions(bestPricePackage, filters?.checkIn ? moment(filters?.checkIn) : undefined, filters?.checkOut ? moment(filters?.checkOut) : undefined)

  const allBonusInclusions = useAllInclusions(commonInclusions, bonusInclusions, tacticalInclusions)

  const showBonusInclusions = showLPCTacticalLabel && allBonusInclusions.length > 0

  const shouldShowInclusions =
  type === OFFER_TYPE_HOTEL ||
    (!!tileInclusions &&
    tileInclusions.length > 0 &&
    !showInclusionUpsell)

  const hotelDescription =
    tileInclusions?.length === undefined ||
    tileInclusions?.length < 3 ?
      name :
      undefined

  const mapSearchUrl = `/search/map?${setSearchParamValue(
    windowSearch,
    queryKeySelectedOfferIds,
    encodeOfferIds([offer]),
  )}`

  let inclusionsCount = 5

  if (maxInclusions) {
    inclusionsCount = maxInclusions
  }

  return (
    <PropertyDetails>
      <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start">
        <Group direction="vertical">
          <Group direction="vertical" fullWidth gap={4}>
            {!showCompact && property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
              <CSSBreakpoint min="desktop">
                <HeaderActions>
                  <NumberRating
                    variant="medium"
                    rating={property.rating}
                    total={property.reviewsTotal}
                    ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
                  />
                </HeaderActions>
              </CSSBreakpoint>
            )}
            <OfferUrgencyLabels offer={offer} />
            <OfferTileLocationSection
              location={location}
              showSearchDistance={showSearchDistance}
              shouldShowMapLink={shouldShowMapButton}
              mapSearchUrl={mapSearchUrl}
              searchTargetDistance={searchTargetDistance}
              searchTargetLabel={searchTargetLabel}
            />
          </Group>
          <div>
            {property && (
              <OfferTilePropertyHeading
                name={property.name}
                descriptiveName={showCompact ? '' : hotelDescription}
                showCompact={showCompact}
              />
            )}
          </div>
        </Group>

        {showCompact && property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
          <NumberRating
            variant="xs"
            rating={property.rating}
            total={property.reviewsTotal}
            onlyNumber
          />
        )}
      </Group>

      {!showCompact && property && showUserReviewsRating(property.rating, property.reviewsTotal) && (
        <CSSBreakpoint max="tablet">
          <NumberRating
            inlineLabel
            variant="xs"
            rating={property.rating}
            total={property.reviewsTotal}
            ratingSubtitle={property.reviewsSource === 'google' ? 'From online reviews' : undefined}
          />
        </CSSBreakpoint>
      )}
      {showLPCTacticalLabel ? <ProductTypeLabel productType="limited_time_special" kind="plain" /> : (!hideAoLabel && (<ProductTypeLabel productType={productType} kind="plain" />))}
      {!showCompact && <SearchTileCancellationPolicy
        cancellationPolicyType={cancellationPolicyType}
        checkInDate={checkInDate}
        timezoneOffset={offer?.property?.timezoneOffset}
        offerType={offer.type}
      />}
      {!showBonusInclusions && (tileInclusions?.length || tileDescription) &&
        shouldShowInclusions && (
          <TileInclusions
            offerTitle={offer.property?.name ? offer.property.name : offer.name}
            offerURL={offerUrl}
            content={tileDescription}
            tileInclusionsList={tileInclusions}
            heading={showCompact ? '' : inclusionsHeading}
            maxInclusionsCount={inclusionsCount}
            maxLineCount={maxInclusionLines}
          />
      )}
      {showBonusInclusions && (
        <TileInclusions
          offerTitle={offer.property?.name ? offer.property.name : offer.name}
          offerURL={offerUrl}
          tileInclusionsList={allBonusInclusions}
          heading={showCompact ? '' : inclusionsHeading}
          maxInclusionsCount={inclusionsCount}
          maxLineCount={maxInclusionLines}
        />
      )}
    </PropertyDetails>
  )
}

export default connect<MappedStateProps, undefined, Props, App.State>(
  (state): MappedStateProps => ({
    shouldShowMapButton: getShowOnMapOfferExperimentVariant(state),
    windowSearch: state.router.location.search,
  }),
)(SearchTilePropertyDetails)
